// src/services/datev/EauNachweisService.ts
// Erzeugt eine Übersicht aller eAU-Anfragen eines Mandanten als PDF,
// vollständig im Browser (pdf-lib). Ersteller ist die XTAX GmbH & Co. KG.
//
// Das Dokument ist für den Kunden bestimmt und dafür gedacht, es zur Akte zu
// nehmen. Es weist je Vorgang aus, was bei der Krankenkasse angefragt wurde und
// was sie zurückgemeldet hat.
//
// Aufbau und Sanitisierung folgen dem bereits eingesetzten
// VersandnachweisService: Standardschriften decken die deutschen Umlaute ab,
// häufige ost- und südosteuropäische Buchstaben werden auf das Grundzeichen
// zurückgeführt. In Zeitarbeits-Belegschaften ist das der Regelfall, nicht die
// Ausnahme.
//
// Reihenfolge der Angaben je Vorgang: zuerst, was für die Entgeltfortzahlung
// zählt (Zeitraum und Art der Bescheinigung), danach die Krankenkasse als
// Ansprechpartner, zuletzt die ergänzenden ärztlichen Angaben.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase } from '../../shared/services/supabase';
import {
  DatevEauFlag,
  DATEV_EAU_FLAG_LABELS
} from './DatevEauService';

interface MeasurableFont {
  widthOfTextAtSize: (text: string, size: number) => number;
}

interface EauVorgang {
  id: string;
  personnel_number: string;
  start_work_incapacity: string;
  follow_up_certification: boolean;
  state: string;
  last_feedback: Record<string, unknown> | null;
  ablehnung_code: string | null;
  ablehnung_text: string | null;
  created_at: string;
}

interface Rueckmeldung {
  eau_uuid: string;
  start_work_incapacity_au: string | null;
  end_work_incapacity_au: string | null;
  actual_end_work_incapacity_au: string | null;
  date_of_diagnosis: string | null;
  flag_current_work_incapacity: number | null;
  accident_at_work: boolean | null;
  assignment_accident_insurance_doctor: boolean | null;
  other_accident: boolean | null;
  start_hospitalisation: string | null;
  end_hospitalisation: string | null;
  initial_certificate: boolean | null;
  automatic_feedback_until: string | null;
  health_insurance_name: string | null;
  contact_name: string | null;
  contact_telephone: string | null;
  fetched_at: string;
}

const TRANSLIT: Record<string, string> = {
  'ł': 'l', 'Ł': 'L', 'đ': 'd', 'Đ': 'D',
  'ı': 'i', 'İ': 'I', 'ş': 's', 'Ş': 'S', 'ğ': 'g', 'Ğ': 'G',
  'ą': 'a', 'ę': 'e', 'ć': 'c', 'ń': 'n', 'ś': 's', 'ź': 'z', 'ż': 'z',
  'Ą': 'A', 'Ę': 'E', 'Ć': 'C', 'Ń': 'N', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z',
  'č': 'c', 'ř': 'r', 'š': 's', 'ž': 'z', 'ě': 'e', 'ů': 'u', 'ď': 'd', 'ť': 't', 'ň': 'n',
  'Č': 'C', 'Ř': 'R', 'Š': 'S', 'Ž': 'Z', 'Ě': 'E', 'Ů': 'U',
  'ő': 'o', 'ű': 'u', 'Ő': 'O', 'Ű': 'U',
  'ā': 'a', 'ē': 'e', 'ī': 'i', 'ō': 'o', 'ū': 'u'
};

const UNDEFINED_CP1252 = new Set([129, 141, 143, 144, 157]);

function sanitize(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';
  let out = '';
  for (const ch of String(input)) {
    if (TRANSLIT[ch] !== undefined) {
      out += TRANSLIT[ch];
      continue;
    }
    const code = ch.charCodeAt(0);
    if (code <= 255 && !UNDEFINED_CP1252.has(code)) {
      out += ch;
    } else {
      out += '?';
    }
  }
  return out;
}

function wrapText(text: string, maxWidth: number, font: MeasurableFont, size: number): string[] {
  const clean = sanitize(text);
  const words = clean.split(/\s+/).filter(w => w.length > 0);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

// Fehlt ein Wert, wird ein leerer String geliefert. Die ausgebende Funktion
// laesst solche Angaben ganz weg, statt einen Platzhalter zu setzen: Ein
// Platzhalter liest sich wie eine Luecke, die noch zu fuellen waere, obwohl es
// zu der Angabe schlicht nichts zu melden gibt.
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const treffer = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (treffer) return `${treffer[3]}.${treffer[2]}.${treffer[1]}`;
  return String(iso);
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin'
  }) + ' Uhr';
}

function jaNein(wert: boolean | null | undefined): string {
  if (wert === null || wert === undefined) return '';
  return wert ? 'Ja' : 'Nein';
}

/**
 * Klartext für den Zustand eines Vorgangs. Bewusst eigene Formulierungen, nicht
 * die der Bildschirmliste: Ein Dokument, das zur Akte genommen wird, muss aus
 * sich heraus verständlich sein.
 */
function zustandText(vorgang: EauVorgang): string {
  switch (vorgang.state) {
    case 'requested':
      return 'Anfrage gestellt, Rückmeldung der Krankenkasse steht aus';
    case 'zwischenmeldung':
      return 'Zwischenmeldung erhalten, die Krankenkasse liefert nach';
    case 'abgeschlossen':
    case 'feedback_received':
      return 'Rückmeldung der Krankenkasse liegt vor';
    case 'cancelled':
      return 'Anfrage storniert';
    case 'abgelehnt':
      return ablehnungText(vorgang.ablehnung_code);
    default:
      return vorgang.state;
  }
}

function ablehnungText(code: string | null): string {
  switch (code) {
    case 'EAU C-PL-0000001':
      // Bewusst ohne Zusatz zur Papierform: Ob und wie der Arbeitnehmer seine
      // Arbeitsunfaehigkeit nachweist, regelt das Verhaeltnis zwischen ihm und
      // seinem Arbeitgeber. Ein Hinweis darauf in diesem Dokument liest sich wie
      // eine gesetzliche Vorgabe und waere eine Aussage, die uns nicht zusteht.
      return 'Kein elektronischer Abruf möglich, der Mitarbeiter war zu Beginn der '
        + 'Arbeitsunfähigkeit privat versichert.';
    case 'EAU C-RE-0000000':
      return 'Zu diesem Beginn wurde bereits eine Anfrage gestellt.';
    case 'EAU C-PL-0000002':
      return 'Zum angegebenen Beginn bestand kein Beschäftigungsverhältnis.';
    case 'EAU C-PL-0000003':
      return 'Zum angegebenen Beginn lagen keine passenden Versicherungsdaten vor.';
    case 'EAU C-PL-0000000':
      return 'Der Mitarbeiter wurde im Lohnsystem nicht gefunden.';
    case 'EAU C-CL-0000000':
      return 'Der Mandant war zum Zeitpunkt der Anfrage nicht für das eAU-Verfahren freigeschaltet.';
    default:
      return 'Die Anfrage wurde nicht angenommen.';
  }
}

export class EauNachweisService {

  /**
   * Erzeugt die Übersicht der eAU-Anfragen eines Mandanten und startet den
   * Download im Browser.
   */
  async generateNachweis(
    customerId: string,
    clientId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Mandant
      const { data: kunde } = await supabase
        .from('customers')
        .select('company, beraternummer, mandantennummer')
        .eq('id', customerId)
        .maybeSingle();

      // 2. Vorgänge
      const { data: vorgangData, error: vorgangError } = await supabase
        .from('datev_eau_requests')
        .select('id, personnel_number, start_work_incapacity, follow_up_certification, state, last_feedback, ablehnung_code, ablehnung_text, created_at')
        .eq('customer_id', customerId)
        .eq('client_id', clientId)
        .order('start_work_incapacity', { ascending: true })
        .limit(100000);

      if (vorgangError) {
        return { success: false, error: `Vorgänge konnten nicht geladen werden: ${vorgangError.message}` };
      }
      const vorgaenge = (vorgangData || []) as EauVorgang[];

      if (vorgaenge.length === 0) {
        return { success: false, error: 'Für diesen Mandanten sind keine eAU-Anfragen vorhanden.' };
      }

      // 3. Rückmeldungen aus der Historie. Ältere Vorgänge, die vor Einführung
      //    der Historie beantwortet wurden, tragen ihre Rückmeldung noch im
      //    Vorgang selbst; dafür gibt es weiter unten den Rückfall.
      const { data: feedbackData } = await supabase
        .from('datev_eau_feedbacks')
        .select('*')
        .eq('customer_id', customerId)
        .order('fetched_at', { ascending: true })
        .limit(100000);

      const proVorgang = new Map<string, Rueckmeldung[]>();
      for (const zeile of ((feedbackData || []) as Rueckmeldung[])) {
        const liste = proVorgang.get(zeile.eau_uuid) || [];
        liste.push(zeile);
        proVorgang.set(zeile.eau_uuid, liste);
      }

      // 4. Namen
      const { data: mitarbeiterData } = await supabase
        .from('datev_employees')
        .select('personalnummer, vorname, nachname')
        .eq('customer_id', customerId)
        .limit(100000);

      const namen = new Map<string, string>();
      for (const m of ((mitarbeiterData || []) as Array<{ personalnummer: string; vorname: string | null; nachname: string | null }>)) {
        const name = `${m.nachname || ''}, ${m.vorname || ''}`.replace(/^, |, $/g, '').trim();
        if (name) namen.set(String(m.personalnummer), name);
      }

      // 5. PDF aufbauen
      const pdf = await PDFDocument.create();
      pdf.setTitle('Übersicht der eAU-Anfragen');
      pdf.setAuthor('XTAX GmbH & Co. KG');
      pdf.setCreator('XTAX GmbH & Co. KG');
      pdf.setProducer('XTAX GmbH & Co. KG');
      pdf.setSubject('Elektronische Arbeitsunfähigkeitsbescheinigungen');
      pdf.setCreationDate(new Date());
      pdf.setModificationDate(new Date());

      const font = await pdf.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

      const W = 595.28;
      const H = 841.89;
      const MARGIN = 50;
      const contentWidth = W - 2 * MARGIN;

      const dark = rgb(0.07, 0.09, 0.15);
      const gray = rgb(0.42, 0.45, 0.5);
      const accent = rgb(0, 0.4, 0.8);
      const lineColor = rgb(0.9, 0.9, 0.92);

      let page = pdf.addPage([W, H]);
      let y = H - MARGIN;

      const ensure = (needed: number) => {
        if (y - needed < MARGIN) {
          page = pdf.addPage([W, H]);
          y = H - MARGIN;
        }
      };

      const writeText = (text: string, x: number, size: number, f: typeof font, color: typeof dark) => {
        page.drawText(sanitize(text), { x, y, size, font: f, color });
      };

      const paragraph = (text: string, size: number, f: typeof font, color: typeof dark, lineHeight: number) => {
        for (const ln of wrapText(text, contentWidth, f, size)) {
          ensure(lineHeight);
          page.drawText(ln, { x: MARGIN, y, size, font: f, color });
          y -= lineHeight;
        }
      };

      const sectionHeader = (title: string) => {
        ensure(34);
        y -= 8;
        writeText(title, MARGIN, 12, fontBold, accent);
        y -= 8;
        page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 0.7, color: lineColor });
        y -= 16;
      };

      // Angabe in einer Zeile, Bezeichnung links, Wert rechts daneben.
      // Ohne Wert entfaellt die Zeile.
      const feld = (label: string, value: string) => {
        if (!value || value.trim().length === 0) return;
        ensure(16);
        page.drawText(sanitize(label), { x: MARGIN, y, size: 9, font: fontBold, color: gray });
        const lines = wrapText(value, contentWidth - 150, font, 10);
        lines.forEach((ln, idx) => {
          if (idx > 0) { y -= 13; ensure(13); }
          page.drawText(ln, { x: MARGIN + 150, y, size: 10, font, color: dark });
        });
        y -= 15;
      };

      // Kopf
      writeText('Übersicht der eAU-Anfragen', MARGIN, 18, fontBold, dark);
      y -= 22;
      writeText('XTAX GmbH & Co. KG - Lohnbuchhaltung', MARGIN, 10, font, gray);
      y -= 16;
      page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 1, color: accent });
      y -= 20;

      feld('Mandant', kunde?.company || clientId);
      feld('Berater / Mandant', clientId);
      feld('Erstellt am', formatDateTime(new Date().toISOString()));
      feld('Anzahl der Vorgänge', String(vorgaenge.length));
      y -= 6;

      paragraph(
        'Diese Übersicht dokumentiert die elektronischen Anfragen von Arbeitsunfähigkeitsbescheinigungen, '
        + 'die für Ihre Mitarbeiter bei den zuständigen Krankenkassen gestellt wurden, sowie deren Rückmeldungen.',
        10, font, gray, 14
      );
      y -= 4;

      // Zusammenfassung
      const zaehler = new Map<string, number>();
      for (const v of vorgaenge) {
        const schluessel = v.state === 'abgelehnt' && v.ablehnung_code === 'EAU C-PL-0000001'
          ? 'Kein Abruf möglich (privat versichert)'
          : zustandText(v).split(',')[0];
        zaehler.set(schluessel, (zaehler.get(schluessel) || 0) + 1);
      }
      sectionHeader('Zusammenfassung');
      for (const [text, anzahl] of Array.from(zaehler.entries()).sort((a, b) => b[1] - a[1])) {
        feld(String(anzahl), text);
      }

      // Vorgänge
      sectionHeader('Vorgänge im Einzelnen');

      for (const v of vorgaenge) {
        const rueckmeldungen = proVorgang.get(v.id) || [];
        // Rückfall auf die im Vorgang gespeicherte Rückmeldung, solange die
        // Historie für ältere Vorgänge noch keine Zeile enthält.
        const ausVorgang = (v.last_feedback as { feedbacks_from_health_insurance?: Array<Record<string, any>> } | null)
          ?.feedbacks_from_health_insurance;
        const letzte: Record<string, any> | null =
          rueckmeldungen.length > 0
            ? (rueckmeldungen[rueckmeldungen.length - 1] as unknown as Record<string, any>)
            : (Array.isArray(ausVorgang) && ausVorgang.length > 0
                ? { ...(ausVorgang[ausVorgang.length - 1].incapacity_for_work || {}),
                    health_insurance_name: ausVorgang[ausVorgang.length - 1].contact_person?.name1_health_insurance ?? null,
                    contact_name: ausVorgang[ausVorgang.length - 1].contact_person?.name ?? null,
                    contact_telephone: ausVorgang[ausVorgang.length - 1].contact_person?.telephone ?? null }
                : null);

        ensure(60);
        y -= 6;
        const name = namen.get(v.personnel_number) || `Personalnummer ${v.personnel_number}`;
        writeText(name, MARGIN, 12, fontBold, dark);
        y -= 15;
        writeText(`Personalnummer ${v.personnel_number}`, MARGIN, 9, font, gray);
        y -= 16;

        // Zuerst das Ergebnis: darum geht es.
        feld('Ergebnis', zustandText(v));

        const flag = letzte?.flag_current_work_incapacity as DatevEauFlag | undefined;
        if (flag) {
          feld('Angabe der Kasse', DATEV_EAU_FLAG_LABELS[flag] || `Kennzeichen ${flag}`);
        }

        // Danach der Zeitraum, der für die Entgeltfortzahlung zählt.
        feld('Beginn laut Meldung an die Kasse', formatDate(v.start_work_incapacity));
        if (letzte) {
          feld('Beginn laut Bescheinigung', formatDate(letzte.start_work_incapacity_au));
          feld('Voraussichtliches Ende', formatDate(letzte.end_work_incapacity_au));
          feld('Tatsächliches Ende', formatDate(letzte.actual_end_work_incapacity_au));
          feld('Art der Bescheinigung',
            letzte.initial_certificate === null || letzte.initial_certificate === undefined
              ? ''
              : (letzte.initial_certificate ? 'Erstbescheinigung' : 'Folgebescheinigung'));
        } else {
          feld('Art der Anfrage', v.follow_up_certification ? 'Folgebescheinigung' : 'Erstbescheinigung');
        }

        // Dann die Kasse als Ansprechpartner für Rückfragen.
        if (letzte?.health_insurance_name) {
          feld('Krankenkasse', String(letzte.health_insurance_name));
          if (letzte.contact_name) feld('Ansprechpartner', String(letzte.contact_name));
          if (letzte.contact_telephone) feld('Telefon', String(letzte.contact_telephone));
        }

        // Zuletzt die ergänzenden ärztlichen Angaben.
        if (letzte) {
          feld('Feststellungsdatum', formatDate(letzte.date_of_diagnosis));
          feld('Arbeitsunfall', jaNein(letzte.accident_at_work));
          feld('Zuweisung Durchgangsarzt', jaNein(letzte.assignment_accident_insurance_doctor));
          feld('Sonstiger Unfall', jaNein(letzte.other_accident));
          if (letzte.start_hospitalisation || letzte.end_hospitalisation) {
            feld('Krankenhausaufenthalt',
              `${formatDate(letzte.start_hospitalisation)} bis ${formatDate(letzte.end_hospitalisation)}`);
          }
          if (letzte.automatic_feedback_until) {
            feld('Nachlieferung durch die Kasse bis', formatDate(letzte.automatic_feedback_until));
          }
          if (rueckmeldungen.length > 1) {
            feld('Rückmeldungen der Kasse', `${rueckmeldungen.length}, jüngste vom ${formatDateTime(rueckmeldungen[rueckmeldungen.length - 1].fetched_at)}`);
          }
        }

        feld('Anfrage gestellt am', formatDateTime(v.created_at));

        ensure(12);
        y -= 4;
        page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 0.5, color: lineColor });
        y -= 10;
      }

      // Hinweis
      sectionHeader('Hinweis');
      paragraph(
        'Die Angaben stammen aus den Rückmeldungen der Krankenkassen über das elektronische Verfahren nach '
        + '§ 109 SGB IV. Vorgänge ohne Rückmeldung sind bei der Kasse angefragt, dort aber noch nicht beantwortet. '
        + 'Für privat versicherte Mitarbeiter sieht das Verfahren keinen elektronischen Abruf vor.',
        10, font, gray, 14
      );
      y -= 6;

      ensure(30);
      page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 0.7, color: lineColor });
      y -= 16;
      paragraph(
        `Erzeugt am ${formatDateTime(new Date().toISOString())}. Erstellt durch die XTAX GmbH & Co. KG.`,
        9, font, gray, 13
      );

      // 6. Download auslösen
      const bytes = await pdf.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const heute = new Date().toISOString().slice(0, 10);
      const filename = `eAU-Uebersicht_${clientId}_${heute}.pdf`;

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true };

    } catch (error) {
      console.error('[eAU-Nachweis] Fehler bei der Erzeugung:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der Erzeugung'
      };
    }
  }
}

export const eauNachweisService = new EauNachweisService();
